/**
 * @license Copyright (c) 2014-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import { EditorConfig } from '@ckeditor/ckeditor5-core/src/editor/editorconfig';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials'; // [shift+enter] brタグを挿入する
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Link from '@ckeditor/ckeditor5-link/src/link';
import List from '@ckeditor/ckeditor5-list/src/documentlist';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import SourceEditing from '@ckeditor/ckeditor5-source-editing/src/sourceediting';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import FindAndReplace from '@ckeditor/ckeditor5-find-and-replace/src/findandreplace';
import Font from '@ckeditor/ckeditor5-font/src/font';
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import GeneralHtmlSupport, { GeneralHtmlSupportConfig } from '@ckeditor/ckeditor5-html-support/src/generalhtmlsupport';
import HtmlComment from '@ckeditor/ckeditor5-html-support/src/htmlcomment';
import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import type { FileLoader } from '@ckeditor/ckeditor5-upload/src/filerepository'
import { download, upload, resizeImage } from '@/admin/file';
import { useNotification } from '@/composition/notification';
import { ref } from '@vue/composition-api';

export const uploadingLists = ref<number[]>([]); // 画像アップロード中の画像IDリスト

class Editor extends ClassicEditor {}

declare interface Config extends EditorConfig {
  toolbar:
    | string[]
    | {
        items?: string[] | undefined;
        shouldNotGroupWhenFull?: boolean | undefined;
        removeItems?: string[] | undefined;
      }
    | undefined;
  htmlSupport: GeneralHtmlSupportConfig;
}
const customAddStringButtonTypes = ['nickname', 'year', 'month', 'date'] as const;
export type CustomAddStringButtonType = typeof customAddStringButtonTypes[number];
function isCustomAddStringButtonType(value: any): value is CustomAddStringButtonType {
  return customAddStringButtonTypes.includes(value);
}
class CustomAddStringButtonPlugin extends Plugin {
  factoryName: CustomAddStringButtonType;
  buttonDisplayName: string;
  addText: string;
  constructor(editor: Editor, factoryName: CustomAddStringButtonType, buttonDisplayName: string, addText: string) {
    super(editor);
    this.factoryName = factoryName;
    this.buttonDisplayName = buttonDisplayName;
    this.addText = addText;
  }
  init() {
    const editor = this.editor;
    if (editor instanceof Editor) {
      editor.ui.componentFactory.add(this.factoryName, () => {
        // The button will be an instance of ButtonView.
        const button = new ButtonView();
        const option: Record<string, unknown> = {
          label: this.buttonDisplayName,
          withText: true,
        };
        /* @ts-ignore */
        button.set(option);
        //Execute a callback function when the button is clicked
        button.on('execute', () => {
          const source = document.querySelector('.ck-source-editing-area');
          if (source) {
            alert('ソースモードでは追加できません。ソースモードを解除してください。');
            return;
          }
          //Change the model using the model writer
          editor.model.change((writer) => {
            //Insert the text at the user's current position
            editor.model.insertContent(writer.createText(this.addText));
          });
        });

        return button;
      });
    }
  }
}

class UploadAdapter {
  loader: FileLoader
  controller: AbortController | null = null;

  constructor(loader: FileLoader) {
    this.loader = loader;
  }

  /**
   * Wysiwyg画像アップロード処理
   * 
   * @remarks
   * 画面上は画像が表示されているので、画像アップロード処理の途中での離脱を防ぐため、
   * 画像アップロードの際にアップロード中リストに追加することで、画像アップロードが完了するまで保存、画面遷移させないようにする
   * Wysiwygコンポーネントはルートコンポーネントにならないため、
   * Wysiwygコンポーネントを画像アップロードありで使用する親コンポーネントでは、ナビゲーションガードを実装する必要がある
   * 
   * @returns ファイルダウンロード用署名付URL
   */
  async upload() {
    const length = uploadingLists.value.length
    // 内部で画像アップロード処理中でも、画面上は画像が表示されているため、画像アップロード中リストに追加する
    // この際に単体のフラグ管理ではダメな理由は、画像アップロードが複数ある場合、アップロードごとにこの関数が呼ばれるため、1つでもアップロード中の画像があるかを管理する必要がある
    uploadingLists.value.push(this.loader.id);
    try {
      // FIXME: 画像を一括でアップロードした際にiOSのSafariでアップロードした画像のパスが重複する（例えば、2枚目の画像が1枚目の画像パスと同じになる）ことがあり、request failed status code 503と出ているため画像アップロード処理を遅延させることで回避を試みる
      // ブラウザに同時に並列で処理できるリクエストに制限があるみたいで、アップロードの同時処理数を制限する機能はないっぽいので、独自にキューに追加して処理するロジックを実装しないといけないかも（setTimeoutでは、前の処理が終わってない可能性もあるため）
      // https://qiita.com/proyuki02/items/50076ed1a276492458b1 こういうライブラリ使ってもいいかも
      await new Promise((resolve) => {
        setTimeout(resolve, 500 * length)
      });
      const file = await this.loader.file
      if (!file) return
      this.controller = new AbortController();
      const filePath = await upload(file);
      if (['image/png', 'image/jpeg'].includes(file.type)) await resizeImage(filePath);
      const path = await download(filePath)
      return {
        default: path,
      }
    } catch (e) {
      // API通信でエラーが発生した場合は、エラーを通知する
      useNotification().error(e);
    } finally {
      // 画像アップロード処理が完了、またはエラーとなったら、画像アップロード中リストから削除する
      const index = uploadingLists.value.indexOf(this.loader.id);
      if (index !== -1) {
        uploadingLists.value.splice(index, 1);
      }
    }
  }

  /**
   * 画像のアップロードが失敗した際の処理
   */
  abort() {
    if (this.controller) {
        this.controller.abort();
    }
  }

}
function UploadAdapterPlugin(editor: any) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader: FileLoader) => {
    return new UploadAdapter( loader );
  };
}

class AddNickNamePlugin extends CustomAddStringButtonPlugin {
  constructor(editor: Editor) {
    super(editor, 'nickname', 'ニックネーム追加', '{{nickname}}');
  }
}
class AddYearPlugin extends CustomAddStringButtonPlugin {
  constructor(editor: Editor) {
    super(editor, 'year', '年追加', '{{year}}');
  }
}
class AddNMonthPlugin extends CustomAddStringButtonPlugin {
  constructor(editor: Editor) {
    super(editor, 'month', '月追加', '{{month}}');
  }
}
class AddDatePlugin extends CustomAddStringButtonPlugin {
  constructor(editor: Editor) {
    super(editor, 'date', '日にち追加', '{{date}}');
  }
}
// Plugins to include in the build.
Editor.builtinPlugins = [
  BlockQuote,
  Bold,
  Essentials,
  Heading,
  Image,
  ImageCaption,
  // ↓スタイルを当てるとカスタムなclassがつくためそれに対応したスタイルをフロント側で別途指定する必要があり、そのため非アクティブ
  // ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  Italic,
  Link,
  List,
  // MediaEmbed,
  PasteFromOffice,
  SourceEditing,
  Table,
  TableToolbar,
  Alignment,
  FindAndReplace,
  Font,
  HorizontalLine,
  GeneralHtmlSupport,
  HtmlComment,
  AddNickNamePlugin,
  AddYearPlugin,
  AddNMonthPlugin,
  AddDatePlugin,
  UploadAdapterPlugin,
];

// Editor configuration.
class Ckeditor {
  itemsSettings = {
    heading: ['heading', '|'],
    defaultItems: [
      'bold',
      'italic',
      'alignment',
      'link',
      'bulletedList',
      'numberedList',
      '|',
      'fontSize',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'outdent',
      'indent',
      'horizontalLine',
      '|',
      'imageUpload',
      'blockQuote',
      'insertTable',
      // 'mediaEmbed',
      'sourceEditing',
      'findAndReplace',
      'undo',
      'redo',
    ],
  };
  settings: Config = {
    fontSize: {
      options: [10, 12, 14, 16, 18, 20],
    },
    toolbar: {
      items: [
        'heading',
        '|',
        'bold',
        'italic',
        'alignment',
        'link',
        'bulletedList',
        'numberedList',
        '|',
        'fontSize',
        'fontColor',
        'fontBackgroundColor',
        '|',
        'outdent',
        'indent',
        'horizontalLine',
        '|',
        'imageUpload',
        'blockQuote',
        'insertTable',
        // 'mediaEmbed',
        'sourceEditing',
        'findAndReplace',
        'undo',
        'redo',
      ],
      // 全てのツールバーを表示する（falseの場合は３点リーダー）
      shouldNotGroupWhenFull: true,
    },
    // リンクに別タブリンクを仕込む
    link: {
      decorators: {
        isExternal: {
          mode: 'manual',
          label: 'Open in a new tab',
          attributes: {
            rel: 'noopener noreferrer',
            target: '_blank',
          },
        },
      },
    },
    // ソースモード切り替え時に、置換されるのを防ぐ
    htmlSupport: {
      allow: [
        {
          name: /^(h[1-6]|p|div|section|article|strong|span|sup|sub|ul|ol|li|dl|dt|dd|table|tbody|tr|th|td|caption|a|img|form|input|iframe)$/,
          attributes: true,
          styles: true,
          classes: true,
        },
      ],
    },
    // 見出し1がh2タグになるのを回避する
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
        { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
      ],
    },
    image: {
      toolbar: ['imageTextAlternative', 'imageStyle:inline', 'imageStyle:block', 'imageStyle:side'],
    },
    table: {
      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
    },
    language: 'ja',
  };

  constructor(props: {
      addStringButtonTypes: CustomAddStringButtonType[];
      toolbarItems: string[];
      enableUploadImages: boolean; 
      removeItems: string[];
    }) {
    const addStringButtonTypes = props.addStringButtonTypes;
    const toolbarItems = props.toolbarItems;
    const removeItems = props.removeItems;
    const enableUploadImages  = props.enableUploadImages;

    if (addStringButtonTypes.some((type) => !isCustomAddStringButtonType(type))) throw 'invalid params';
    let items = toolbarItems.length === 0 ? this.itemsSettings.heading : [];
    addStringButtonTypes.forEach((addStringButtonType) => {
      items.push(addStringButtonType);
      items.push('|');
    });
    items = items.concat(toolbarItems.length === 0 ? this.itemsSettings.defaultItems : toolbarItems);
    if(!enableUploadImages){
      items.splice(items.indexOf("imageUpload"), 1)
    }
    this.settings.toolbar = {
      items,
      removeItems,
      shouldNotGroupWhenFull: true,
    };
  }
}

export const useCkeditor = (props: {
  addStringButtonTypes: CustomAddStringButtonType[];
  toolbarItems: string[];
  removeItems: string[];
  enableUploadImages: boolean;
}) => {
  const ckeditor = new Ckeditor(props);
  return ckeditor;
};

Editor.defaultConfig = new Ckeditor({
    addStringButtonTypes:[],
    toolbarItems:[],
    enableUploadImages:false,
    removeItems: []
  }).settings;

export default Editor;
